import { useCallback, useEffect, useState } from 'react'
import type { DocumentChantier } from '../lib/types'
import { messageClair } from '../lib/televersement'
import {
  JOURS_CORBEILLE,
  chargerCorbeille,
  dateEffacement,
  restaurer,
  supprimerDocument,
  viderExpires,
  type DocumentCorbeille
} from '../lib/suppression'

function jour(d: Date | string): string {
  return new Date(d).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long' })
}

/**
 * Corbeille d'un onglet (Plans ou Devis), pour les administrateurs.
 * Masquée quand elle est vide. Vide d'elle-même ce qui dépasse 30 jours.
 */
export function Corbeille({
  chantierId,
  types,
  version,
  changement
}: {
  chantierId: string
  types: DocumentChantier['type'][]
  /** Change quand l'onglet met un document à la corbeille : relecture. */
  version: number
  /** Appelé après une restauration, pour relire la liste de l'onglet. */
  changement: () => void
}) {
  const [docs, setDocs] = useState<DocumentCorbeille[]>([])
  const [ouverte, setOuverte] = useState(false)
  const [occupe, setOccupe] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const cleTypes = types.join(',')

  const charger = useCallback(async () => {
    try {
      let liste = await chargerCorbeille(chantierId, cleTypes.split(',') as DocumentChantier['type'][])
      if ((await viderExpires(liste)) > 0) {
        liste = await chargerCorbeille(chantierId, cleTypes.split(',') as DocumentChantier['type'][])
      }
      setDocs(liste)
    } catch {
      // Migration 008 pas encore faite ou pas de réseau : pas de corbeille affichée.
      setDocs([])
    }
  }, [chantierId, cleTypes])

  useEffect(() => {
    void charger()
  }, [charger, version])

  async function remettre(d: DocumentCorbeille) {
    setOccupe(d.id)
    setErreur(null)
    try {
      await restaurer(d)
      changement()
    } catch (e) {
      setErreur("La restauration a échoué : " + messageClair(e))
    } finally {
      setOccupe(null)
      await charger()
    }
  }

  async function effacer(d: DocumentCorbeille) {
    if (
      !window.confirm(
        `Supprimer définitivement « ${d.nom} » ?\n\nLe fichier, ses pages, ses annotations, ses liens et son texte seront effacés pour toute l'équipe.\n\nCette action ne peut pas être annulée.`
      )
    )
      return
    setOccupe(d.id)
    setErreur(null)
    try {
      await supprimerDocument(d)
    } catch (e) {
      setErreur('La suppression a échoué : ' + messageClair(e))
    } finally {
      setOccupe(null)
      await charger()
    }
  }

  if (docs.length === 0) return null

  return (
    <div style={{ marginTop: 24 }}>
      <button className="retour" style={{ textDecoration: 'underline' }} onClick={() => setOuverte(!ouverte)}>
        {ouverte ? '▾' : '▸'} Corbeille ({docs.length})
      </button>
      {ouverte && (
        <>
          <p className="meta" style={{ margin: '6px 0 10px' }}>
            Visible des administrateurs seulement. Chaque document est effacé pour de bon {JOURS_CORBEILLE} jours
            après avoir été retiré.
          </p>
          {erreur && <p className="avis erreur" style={{ marginBottom: 10 }}>{erreur}</p>}
          <div className="liste" style={{ gap: 4 }}>
            {docs.map((d) => (
              <div key={d.id} className="carte bande-nulle" style={{ padding: '9px 12px' }}>
                <p className="titre" style={{ overflowWrap: 'anywhere' }}>{d.nom}</p>
                <p className="meta">
                  <span>
                    Retiré le {jour(d.supprime_le)}
                    {d.supprimeur?.nom ? ` par ${d.supprimeur.nom}` : ''}
                  </span>
                  <span className="pt" />
                  <span>effacé le {jour(dateEffacement(d))}</span>
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button
                    className="bouton"
                    style={{ padding: '7px 12px' }}
                    disabled={occupe !== null}
                    onClick={() => void remettre(d)}
                  >
                    {occupe === d.id ? '…' : 'Restaurer'}
                  </button>
                  <button
                    className="bouton creux"
                    style={{ padding: '7px 12px' }}
                    disabled={occupe !== null}
                    onClick={() => void effacer(d)}
                  >
                    Supprimer définitivement
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
